'use client'

import React, { useContext, useMemo } from 'react'

import { LinkContext } from '../../contexts'
import { THEME_COLORS_ENUM } from '../../types/colors'
import { cn } from '../../utils/cn'
import { ensureIconsSpriteInjected } from '../../utils/icons-sprite'

/** Solid = filled background, Outline = transparent with border, Transparent = no border, white hover */
export enum M_BUTTON_VARIANT_ENUM {
	SOLID = 'solid',
	OUTLINE = 'outline',
	TRANSPARENT = 'transparent',
	TRANSPARENT_OUTLINE = 'transparent-outline'
}

/** Arrow icon position (maps to symbols in icons.svg) */
export enum M_BUTTON_ARROW_ENUM {
	TOP = 'top',
	TOP_RIGHT = 'top-right',
	RIGHT = 'right',
	RIGHT_DOWN = 'right-down',
	DOWN = 'down',
	BOTTOM_LEFT = 'bottom-left',
	LEFT = 'left',
	TOP_LEFT = 'top-left'
}

const arrowIconSymbolIds: Record<M_BUTTON_ARROW_ENUM, string> = {
	[M_BUTTON_ARROW_ENUM.TOP]: 's_ar2_u',
	[M_BUTTON_ARROW_ENUM.TOP_RIGHT]: 's_ar2_ru',
	[M_BUTTON_ARROW_ENUM.RIGHT]: 's_ar2_r',
	[M_BUTTON_ARROW_ENUM.RIGHT_DOWN]: 's_ar2_rd',
	[M_BUTTON_ARROW_ENUM.DOWN]: 's_ar2_d',
	[M_BUTTON_ARROW_ENUM.BOTTOM_LEFT]: 's_ar2_ld',
	[M_BUTTON_ARROW_ENUM.LEFT]: 's_ar2_l',
	[M_BUTTON_ARROW_ENUM.TOP_LEFT]: 's_ar2_lu'
}

export interface IButtonProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
	children: React.ReactNode
	href: string
	/** Theme color (no default) */
	color?: THEME_COLORS_ENUM
	/** Solid (filled), outline, or transparent – default solid */
	variant?: M_BUTTON_VARIANT_ENUM
	/** Arrow icon position. */
	icon?: M_BUTTON_ARROW_ENUM
	/** Base URL of SVG sprite (e.g. "/assets/icons.svg"). If omitted and icon is set, the bundled sprite is injected into the document (same-origin, works in Next.js). */
	iconsSpriteUrl?: string
	className?: string
	/** Custom link component (e.g. next/link). Defaults to <a>. */
	linkComponent?: React.ElementType<React.AnchorHTMLAttributes<HTMLAnchorElement>>
}

function getColorClass(
	color: THEME_COLORS_ENUM | undefined,
	variant: M_BUTTON_VARIANT_ENUM
): string {
	if (variant === M_BUTTON_VARIANT_ENUM.TRANSPARENT) return 'transparent'
	if (variant === M_BUTTON_VARIANT_ENUM.TRANSPARENT_OUTLINE) return 'transparent-outline'
	if (color == null) return ''
	const colorClass = typeof color === 'string' ? color.toLowerCase() : ''
	if (!colorClass) return ''
	return variant === M_BUTTON_VARIANT_ENUM.OUTLINE ? `${colorClass}-outline` : colorClass
}

export const MButton: React.FC<IButtonProps> = ({
	children,
	href,
	color,
	variant = M_BUTTON_VARIANT_ENUM.SOLID,
	icon,
	iconsSpriteUrl,
	className,
	linkComponent: linkComponentProp,
	...anchorProps
}) => {
	const linkFromContext = useContext(LinkContext)
	const LinkComponent = linkComponentProp ?? linkFromContext ?? 'a'

	const colorClass = useMemo(() => getColorClass(color, variant), [color, variant])
	const classes = useMemo(() => cn('m-button', colorClass, className), [colorClass, className])

	const showIcon = icon != null
	const symbolId = showIcon ? arrowIconSymbolIds[icon] : null
	const iconHref = (() => {
		if (!symbolId) return null
		if (iconsSpriteUrl) {
			return `${iconsSpriteUrl.replace(/#.*$/, '')}#${symbolId}`
		}
		ensureIconsSpriteInjected()
		return `#${symbolId}`
	})()
	const Tag = LinkComponent

	return (
		<Tag href={href} className={classes} {...anchorProps}>
			<span className={showIcon ? 'has-icon' : ''}>{children}</span>
			{showIcon && iconHref && (
				<span className='sico-wrapper'>
					<span>
						<svg className='sico' aria-hidden>
							<use href={iconHref} />
						</svg>
					</span>
					<span>
						<svg className='sico' aria-hidden>
							<use href={iconHref} />
						</svg>
					</span>
				</span>
			)}
		</Tag>
	)
}

MButton.displayName = 'MButton'
